import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  auth,
  db,
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  onAuthStateChanged,
  serverTimestamp
} from '../firebase';

const UserContext = createContext(null);

// Role hierarchy for permission checks
// super_admin can manage everything including other super_admins
// admin can do everything except manage super_admin roles
const ROLE_HIERARCHY = {
  super_admin: 5,
  admin: 4,
  customer_admin: 3,
  customer_technician: 2,
  customer_viewer: 1
};

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);           // Firebase auth user
  const [userProfile, setUserProfile] = useState(null);  // Firestore user doc
  const [loading, setLoading] = useState(true);
  const [portfolioRoles, setPortfolioRoles] = useState({}); // { portfolioId: role }

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        // Fetch or create user profile in Firestore
        const userDocRef = doc(db, 'users', firebaseUser.uid);
        const userSnap = await getDoc(userDocRef);

        if (userSnap.exists()) {
          setUserProfile(userSnap.data());
        } else {
          // User exists in Auth but not Firestore - create profile
          // This handles migration from old Realtime DB users
          const newProfile = {
            email: firebaseUser.email,
            name: firebaseUser.displayName || firebaseUser.email.split('@')[0],
            createdAt: serverTimestamp()
          };
          await setDoc(userDocRef, newProfile);
          setUserProfile(newProfile);
        }
      } else {
        setUserProfile(null);
        setPortfolioRoles({});
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Listen to user profile changes in real-time
  useEffect(() => {
    if (!user) return;

    const unsubscribe = onSnapshot(
      doc(db, 'users', user.uid),
      (docSnap) => {
        if (docSnap.exists()) {
          setUserProfile(docSnap.data());
        }
      },
      (error) => {
        console.error('Error listening to user profile:', error);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // Check if user is super admin (highest level - can manage other super admins)
  const isSuperAdmin = () => {
    return userProfile?.role === 'super_admin';
  };

  // Check if user is admin (can do everything except manage super admins)
  const isAdmin = () => {
    return userProfile?.role === 'admin';
  };

  // Check if user has system-level admin access (super_admin or admin)
  const isSystemAdmin = () => {
    return userProfile?.role === 'super_admin' || userProfile?.role === 'admin';
  };

  // Check if user can manage super admin roles (only super_admin can)
  const canManageSuperAdmins = () => {
    return userProfile?.role === 'super_admin';
  };

  // Get user's role in a specific portfolio
  const getPortfolioRole = (portfolioMembers) => {
    if (!user || !portfolioMembers) return null;
    const membership = portfolioMembers[user.uid];
    return membership?.role || null;
  };

  // Check if user has access to a portfolio
  const canAccessPortfolio = (portfolioMembers) => {
    if (isSystemAdmin()) return true;
    return getPortfolioRole(portfolioMembers) !== null;
  };

  // Check if user can edit in a portfolio (admin or technician)
  const canEditInPortfolio = (portfolioMembers) => {
    if (isSystemAdmin()) return true;
    const role = getPortfolioRole(portfolioMembers);
    return role === 'customer_admin' || role === 'customer_technician';
  };

  // Check if user can manage users in a portfolio (admin only)
  const canManageUsersInPortfolio = (portfolioMembers) => {
    if (isSystemAdmin()) return true;
    return getPortfolioRole(portfolioMembers) === 'customer_admin';
  };

  // Check if user can invite a specific role
  const canInviteRole = (targetRole, portfolioMembers) => {
    if (isSystemAdmin()) return true;

    const myRole = getPortfolioRole(portfolioMembers);
    if (myRole !== 'customer_admin') return false;

    // Customer admins can only invite technicians and viewers
    return targetRole === 'customer_technician' || targetRole === 'customer_viewer';
  };

  // Check if user can modify another user's role
  const canModifyUserRole = (targetUserId, portfolioMembers) => {
    if (isSystemAdmin()) return true;
    if (!user) return false;

    // Can't modify yourself
    if (targetUserId === user.uid) return false;

    const myRole = getPortfolioRole(portfolioMembers);
    if (myRole !== 'customer_admin') return false;

    // Customer admins can only modify technicians and viewers
    const targetRole = portfolioMembers[targetUserId]?.role;
    return targetRole === 'customer_technician' || targetRole === 'customer_viewer';
  };

  // Get display name for a role
  const getRoleDisplayName = (role) => {
    const names = {
      super_admin: 'Super Admin',
      admin: 'Admin',
      customer_admin: 'Portfolio Admin',
      customer_technician: 'Contractor',
      customer_viewer: 'Viewer'
    };
    return names[role] || role;
  };

  // Get available roles for invitation based on current user's permissions
  const getInvitableRoles = (portfolioMembers) => {
    if (isSystemAdmin()) {
      return [
        { value: 'customer_admin', label: 'Portfolio Admin' },
        { value: 'customer_technician', label: 'Contractor' },
        { value: 'customer_viewer', label: 'Viewer' }
      ];
    }

    const myRole = getPortfolioRole(portfolioMembers);
    if (myRole === 'customer_admin') {
      return [
        { value: 'customer_technician', label: 'Contractor' },
        { value: 'customer_viewer', label: 'Viewer' }
      ];
    }

    return [];
  };

  const value = {
    user,
    userProfile,
    loading,
    isSuperAdmin,
    isAdmin,
    isSystemAdmin,
    canManageSuperAdmins,
    getPortfolioRole,
    canAccessPortfolio,
    canEditInPortfolio,
    canManageUsersInPortfolio,
    canInviteRole,
    canModifyUserRole,
    getRoleDisplayName,
    getInvitableRoles,
    ROLE_HIERARCHY
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

export default UserContext;
